import React from 'react'
import './src/styles/global.css'
import wrapWithProvider, { getStore } from './utils/wrapWithProvider'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import withLocationProvider from './utils/withLocationProvider'
import { scheduleQualtricsSpaReevaluate } from './utils/qualtricsSpa'

export const onInitialClientRender = () => {
  const loader = document.getElementById('___loader')
  if (loader) {
    loader.classList.add('main_app_loader')
    loader.classList.add('close')
  }
}

export const wrapRootElement = (args) =>
  withLocationProvider(wrapWithProvider(args))

/**
 * Por defecto Gatsby manda el scroll al tope en cada navegacion de pagina. El
 * selector de cantidad de la PLP de Planes navega entre /planes-portabilidad y
 * /grupales, asi que en mobile --donde el selector es sticky-- cambiar de
 * cantidad sacaba al usuario de la seccion que estaba mirando.
 *
 * Solo se desactiva para las navegaciones que piden `preserveScroll` en su
 * state; el resto del sitio conserva el scroll-to-top de siempre.
 */
export const shouldUpdateScroll = ({ routerProps }) => {
  if (routerProps?.location?.state?.preserveScroll) {
    return false
  }
  return true
}

const VERSION = process.env.GATSBY_APP_VERSION || 'dev'
const HOSTS_WITH_DATADOG = ['store.wom.cl', 'store-srv.wom.cl']
const ENABLE_DATADOG = true
const ENABLE_DD_LOGS = true

let datadogUserFingerprint = null
let isDatadogUserSyncSubscribed = false

const isDatadogEnabledForCurrentHost = () => {
  if (typeof window === 'undefined') return false
  return ENABLE_DATADOG && HOSTS_WITH_DATADOG.includes(window.location.hostname)
}

const hasLoginToken = () => {
  if (typeof document === 'undefined') return false

  try {
    const tokenCookie = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith('TOKEN='))
    const token = tokenCookie?.slice('TOKEN='.length)
    return Boolean(token && token !== '0')
  } catch {
    return false
  }
}

const getStoredUserInfo = () => {
  if (typeof window === 'undefined') return null

  try {
    const storedUserInfo = window.localStorage.getItem('userInfo')
    if (!storedUserInfo) return null

    const userInfo = JSON.parse(storedUserInfo)
    return userInfo && typeof userInfo === 'object' && !Array.isArray(userInfo)
      ? userInfo
      : null
  } catch {
    return null
  }
}

const getDatadogUser = (state) => {
  const loggedInUser = hasLoginToken() ? getStoredUserInfo() : null
  const checkoutV2 = state?.WomCheckoutV2
  const checkoutV2User = checkoutV2?.user
  const checkoutV2Document = checkoutV2?.planZero?.document
  const checkoutV2Rut =
    checkoutV2User?.rut ||
    (checkoutV2Document?.type === 'RUT' ? checkoutV2Document?.value : '')
  const legacyCheckoutUser = state?.checkout?.checkout
  const cartInputUser = state?.coreCommonInfo?.addCartInputInfo

  let userInfo = null
  if (loggedInUser?.rut) {
    userInfo = loggedInUser
  } else if (checkoutV2Rut) {
    userInfo = { ...checkoutV2User, rut: checkoutV2Rut }
  } else if (legacyCheckoutUser?.rut) {
    userInfo = legacyCheckoutUser
  } else if (cartInputUser?.rut) {
    userInfo = cartInputUser
  }

  if (!userInfo?.rut) return null

  const rut = String(userInfo.rut).replace(/\./g, '').replace(/\s/g, '')
  if (!rut) return null

  const name = `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim()
  const email = String(userInfo.email || '').trim()
  const user = { id: rut, rut }

  if (name) user.name = name
  if (email) user.email = email

  return user
}

const syncDatadogUser = (state) => {
  if (!isDatadogEnabledForCurrentHost()) return

  const user = getDatadogUser(state)
  const fingerprint = user ? JSON.stringify(user) : ''
  if (fingerprint === datadogUserFingerprint) return

  try {
    if (user) {
      datadogRum.setUser(user)
    } else {
      datadogRum.clearUser()
    }
    datadogUserFingerprint = fingerprint
  } catch { }
}

const startDatadogUserSync = () => {
  if (!isDatadogEnabledForCurrentHost()) return

  const store = getStore()
  if (!store) return

  syncDatadogUser(store.getState())

  if (!isDatadogUserSyncSubscribed) {
    store.subscribe(() => syncDatadogUser(store.getState()))
    isDatadogUserSyncSubscribed = true
  }
}

export const onClientEntry = () => {
  // Prueba temporal: desactiva completamente Datadog RUM y Logs para aislar
  // si su instrumentacion de red participa en los XHR que quedan pendientes.

  function generateUUID() {
    try { return crypto.randomUUID() } catch { }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
  function getPersistentCID() {
    try {
      const KEY = 'cid'
      let v = localStorage.getItem(KEY)
      if (!v) { v = generateUUID(); localStorage.setItem(KEY, v) }
      return v
    } catch { return generateUUID() }
  }
  function getTabCID() {
    try {
      const KEY = 'cid_tab'
      let v = sessionStorage.getItem(KEY)
      if (!v) { v = generateUUID(); sessionStorage.setItem(KEY, v) }
      return v
    } catch { return generateUUID() }
  }

  if (!isDatadogEnabledForCurrentHost()) {
    const origNavigate = window.___navigate
    window.___navigate = function (to, options) {
      let destino = to
      if (typeof destino === 'string' && destino.startsWith(window.location.origin)) {
        destino = destino.replace(window.location.origin, '')
      }
      return origNavigate.call(this, destino, options)
    }
    return
  }

  const CID = getPersistentCID()
  const CID_TAB = getTabCID()
  const VERSION = process.env.GATSBY_APP_VERSION || 'dev'

  // --- RUM ---
  datadogRum.init({
    applicationId: 'f9d6c600-0ebc-4999-acae-a2e4ba24bc88',
    clientToken: 'pub5df082e7e105a668bfa932983efd91a1',
    site: 'datadoghq.com',
    service: 'ecommerce',
    env: 'prod',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    version: VERSION,

  })
  // Adjuntar CIDs a RUM
  try {
    datadogRum.addRumGlobalContext?.('cid', CID)
    datadogRum.addRumGlobalContext?.('cid_tab', CID_TAB)
  } catch { }

  // ---- BRIDGES DE ERRORES
  ; (function attachUnhandledRejectionBridge() {
    function safeStringify(v) { try { return JSON.stringify(v) } catch { return String(v) } }
    window.addEventListener('unhandledrejection', (e) => {
      try {
        const r = e?.reason
        const err = r instanceof Error ? r : new Error(typeof r === 'string' ? r : safeStringify(r))
        datadogRum?.addError?.(err, {
          origin: 'unhandledrejection',
          non_error: !(r instanceof Error),
          cid: CID, cid_tab: CID_TAB,
          page_path: window.location?.pathname,
        })
      } catch { }
    })
  })()

    ; (function attachWindowErrorBridge() {
      function safeStringify(v) { try { return JSON.stringify(v) } catch { return String(v) } }
      window.addEventListener('error', (e) => {
        try {
          if (!(e?.error instanceof Error)) {
            const msg = e?.message ?? safeStringify(e?.error)
            const err = new Error(typeof msg === 'string' ? msg : safeStringify(msg))
            datadogRum?.addError?.(err, {
              origin: 'window.error',
              non_error: true,
              cid: CID, cid_tab: CID_TAB,
              page_path: window.location?.pathname,
            })
          }
        } catch { }
      })
    })()

  // --- LOGS ---
  if (ENABLE_DD_LOGS) {
    datadogLogs.init({
      clientToken: 'pub8ef92c6dda45bfb4e30d8c04346fcf3b',
      site: 'datadoghq.com',
      service: 'ecommerce',
      env: 'prod',
      forwardErrorsToLogs: false,
      sampleRate: 0,
      version: VERSION,
    })

    try { window.__ddLogs = datadogLogs; window.__ddRum = datadogRum } catch { }

    // Contexto global para todos los logs
    try {
      const ctx = datadogRum?.getInternalContext?.()
      datadogLogs.setLoggerGlobalContext?.({
        session_id: ctx?.session_id || undefined,
        rum_view_id: ctx?.view?.id || undefined,
        application_id: ctx?.application_id || undefined,
        cid: CID,
        cid_tab: CID_TAB,
      })
    } catch { }

    // Promesas rechazadas silenciosas
    window.addEventListener('unhandledrejection', (e) => {
      try {
        datadogLogs.logger.error('unhandledrejection', {
          reason: String(e?.reason),
          stack: e?.reason?.stack,
          page_path: window.location?.pathname,
          cid: CID, cid_tab: CID_TAB,
        })
      } catch { }
    })

      // --- Instrumentación fetch ---
      ; (function instrumentFetchErrors() {
        if (!window.fetch || !datadogLogs?.logger) return
        if (window.__dd_fetch_patched) return
        window.__dd_fetch_patched = true

        const logger = datadogLogs.logger
        const original = window.fetch.bind(window)

        function parseUrl(u) {
          try { return new URL(u, window.location.href) } catch { return null }
        }
        // Ignorar intakes de monitoreo (Datadog/Instana)
        function isMonitoringIntake(u) {
          try {
            const h = u.hostname
            const href = u.href
            if (h.includes('datadoghq') && /(intake|rum|logs|replay)/i.test(href)) return true
            if (h.includes('eum.instana.io')) return true
            return false
          } catch { return false }
        }
        // Lee IDs de respuesta del server
        function pickRespIds(headers) {
          try {
            return {
              server_correlation_id: headers.get('x-correlation-id') || undefined,
              server_request_id: headers.get('x-request-id') || headers.get('x-amzn-requestid') || undefined,
            }
          } catch {
            return {}
          }
        }

        window.fetch = async (input, init) => {
          const rawUrl = typeof input === 'string' ? input : input?.url
          const u = parseUrl(rawUrl) || { href: String(rawUrl || ''), hostname: '', pathname: String(rawUrl || '') }

          // No interceptar tráfico de monitoreo
          if (isMonitoringIntake(u)) {
            return original(input, init)
          }

          const method = (init?.method || (typeof input !== 'string' ? input?.method : 'GET') || 'GET').toUpperCase()
          const pathname = u.pathname || String(rawUrl || '')
          const href = u.href || String(rawUrl || '')
          const t0 = performance.now()

          try {
            const res = await original(input, init)
            const ms = Math.round(performance.now() - t0)
            if (!res.ok) {
              logger[res.status >= 500 ? 'error' : 'warn'](
                `HTTP ${res.status} ${pathname}`,
                {
                  endpoint: href,
                  status: res.status,
                  method,
                  dur_ms: ms,
                  page_path: window.location?.pathname,
                  cid: CID, cid_tab: CID_TAB,
                  ...pickRespIds(res.headers),
                }
              )
            }
            return res
          } catch (err) {
            const ms = Math.round(performance.now() - t0)
            logger.error(`HTTP network error ${pathname}`, {
              endpoint: href,
              method,
              dur_ms: ms,
              message: err?.message,
              stack: err?.stack,
              page_path: window.location?.pathname,
              cid: CID, cid_tab: CID_TAB,
            })
            throw err
          }
        }
      })()

    // Ping de arranque
    try {
      datadogLogs.logger.info('logging_bootstrap', {
        t: Date.now(),
        page_path: window.location?.pathname,
        cid: CID, cid_tab: CID_TAB,
      })
    } catch { }
  }

  // patch navigate
  const origNavigate = window.___navigate
  window.___navigate = function (to, options) {
    let destino = to
    if (typeof destino === 'string' && destino.startsWith(window.location.origin)) {
      destino = destino.replace(window.location.origin, '')
    }
    return origNavigate.call(this, destino, options)
  }
}

/** Re-evalúa Site Intercept Qualtrics en navegación cliente (SPA). */
export const onRouteUpdate = () => {
    // get state
    const state = getStore()?.getState()
    startDatadogUserSync()
    // const useBrandsFilter = useBrandsFilter()
    const qualtricsProperties = state?.magento?.paymentMethod ? state?.magento?.paymentMethod : null
    const userInfo = state?.checkout?.checkout
    // pasar como parámetros las propiedades necesarias para evaluar que se debe mostrar el Site Intercept

    if(!qualtricsProperties) {
        scheduleQualtricsSpaReevaluate()
        return
    }

    const group1 = ['simOnly', 'simOnlyPortIn', 'twoBuyOne', 'newConnection']
    const group2 = ['hsRenewal', 'renew', 'hsPlan', 'hsPlanPortIn', 'prePaid', 'salesGood']
    
    if(group1.includes(qualtricsProperties) || group2.includes(qualtricsProperties)) {
        const qualtricParamQuest = group1?.includes(qualtricsProperties) ? 'Quest1' : 'Quest2'
        scheduleQualtricsSpaReevaluate({ page: qualtricParamQuest, firstName: userInfo?.firstName, lastName: userInfo?.lastName, rut: userInfo?.rut, purchaseType: qualtricsProperties })
        return
    }

    scheduleQualtricsSpaReevaluate()
}
